// Contracts — kids PACKER bridge (2026-06-28). Two surfaces, both X-Admin-Secret
// gated (machine-to-machine, no Bearer): the external kids-packer toggles
// sessions.packed and pulls per-day session metadata (child appearance + the 4
// captures with lux/expression/logged_ts + the moderator's pid for file tagging).
// Shapes snapped from migrations/0002 (sessions, captures, children, users).
import { z } from 'zod';
import { KidsLightingSchema } from './enums';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// POST /api/kids/_admin/pack {sessionId, packed} → flip sessions.packed.
export const KidsAdminPackReqSchema = z.object({
  sessionId: z.number().int(),
  packed: z.boolean(),
});
export type KidsAdminPackReq = z.infer<typeof KidsAdminPackReqSchema>;

export const KidsAdminPackResSchema = z.object({
  ok: z.literal(true),
  sessionId: z.number().int(),
  packed: z.boolean(),
});
export type KidsAdminPackRes = z.infer<typeof KidsAdminPackResSchema>;

// POST /api/kids/export/v1/pack {ids:[...]} → batch-flip sessions.packed=1 for a
// day's worth of shipped sessions (export-token or admin gated). 1..500 int ids;
// only existing ids are updated. `updated` = rows actually flipped (idempotent —
// re-sending already-packed ids yields updated=0 for those).
export const KidsExportPackReqSchema = z.object({
  ids: z.array(z.number().int()).min(1).max(500),
});
export type KidsExportPackReq = z.infer<typeof KidsExportPackReqSchema>;

export const KidsExportPackResSchema = z.object({
  ok: z.literal(true),
  updated: z.number().int(),
});

// POST /api/kids/export/v1/claim {device, limit} → DISTRIBUTED WORK-LEASE. With
// several packer machines running, each claims its next batch so no two devices
// grab the same session. Atomic on the server; returns the claimed sessions in
// the SAME shape as /sessions (KidsExportSessionsRes.sessions items). `device`
// identifies the caller (hostname etc.) for lease ownership / debugging.
export const KidsExportClaimReqSchema = z.object({
  device: z.string().min(1).max(120),
  limit: z.number().int().min(1).max(50),
});
export type KidsExportClaimReq = z.infer<typeof KidsExportClaimReqSchema>;

// POST /api/kids/export/v1/release {ids} → return claimed-but-unpacked sessions
// to the pool (device gave up). Clears the lease only; never touches packed.
export const KidsExportReleaseReqSchema = z.object({
  ids: z.array(z.number().int()).min(1).max(500),
});
export type KidsExportReleaseReq = z.infer<typeof KidsExportReleaseReqSchema>;

export const KidsExportReleaseResSchema = z.object({
  ok: z.literal(true),
  released: z.number().int(),
});
export type KidsExportPackRes = z.infer<typeof KidsExportPackResSchema>;

// GET /api/kids/export/v1/sessions?date=&office=&moderatorId= → metadata for the
// packer. `moderatorId` (users.id, optional) narrows to one moderator's sessions
// so the packer can pull a single mod's day.
export const KidsExportSessionsQuerySchema = z.object({
  date: z.string().regex(DATE_RE),
  office: z.string().trim().max(60).optional(),
  moderatorId: z.string().trim().max(64).optional(),
});
export type KidsExportSessionsQuery = z.infer<typeof KidsExportSessionsQuerySchema>;

// GET /api/kids/export/v1/moderators → list of moderators (for the packer's
// dropdown). pid is the 2-digit file tag; name is the display label. PII-light:
// no email/phone (the packer only needs id↔label↔pid).
export const KidsExportModeratorSchema = z.object({
  id: z.string(),
  pid: z.string().nullable(),
  name: z.string(),
  // TG @username (normalized, no leading '@') + IANA timezone — collected at
  // registration (POST /api/kids/me/profile). The packer uses tgLogin to DM the
  // moderator and timezone to schedule "waiting today/this week" sends. null until
  // the moderator fills their profile.
  tgLogin: z.string().nullable(),
  timezone: z.string().nullable(),
  // TG private-chat id bound via the bot's /start webhook (0010). The packer
  // sends per-moderator "your sessions are packed" DMs to this id; null until
  // the moderator starts the bot. Optional so pre-notify packers keep validating.
  tgChatId: z.string().nullable().optional(),
});
export type KidsExportModerator = z.infer<typeof KidsExportModeratorSchema>;

export const KidsExportModeratorsResSchema = z.object({
  moderators: z.array(KidsExportModeratorSchema),
});
export type KidsExportModeratorsRes = z.infer<typeof KidsExportModeratorsResSchema>;

// POST /api/kids/export/v1/client-status {statuses} → stamp the client's
// tracker verdict onto sessions (Tim 04.07). `tag` is the tracker Session_id:
// 6+ digits <modPid:2><seq:4+> (the client PID without the leading 'P').
// Idempotent: re-sending the same verdict yields changed=false for that tag.
// verdict values mirror the master tracker Status column; `invoiced` rides as
// a SEPARATE boolean (Payment column) — the client invoices whole batches,
// rejected rows included, so it is an axis, not a status value (0018).
export const KidsClientStatusSchema = z.enum(['accepted', 'rejected', 'pending']);
export type KidsClientStatus = z.infer<typeof KidsClientStatusSchema>;

export const KidsExportClientStatusReqSchema = z.object({
  statuses: z
    .array(
      z.object({
        tag: z.string().trim().regex(/^\d{6,}$/, 'tag = <modPid:2><seq:4+> digits'),
        status: KidsClientStatusSchema,
        invoiced: z.boolean().optional().default(false),
      }),
    )
    .min(1)
    // Cap 100 (was 1000): each changed row now drives a finance reconcile (~10 D1
    // statements incl. the earnings cascade), so a big batch would blow the Worker's
    // ~1000-subrequest budget. The tracker-sync script must chunk into ≤100 per call.
    .max(100),
});
export type KidsExportClientStatusReq = z.infer<typeof KidsExportClientStatusReqSchema>;

export const KidsExportClientStatusResSchema = z.object({
  ok: z.literal(true),
  updated: z.number().int(),  // rows whose verdict actually CHANGED this call
  matched: z.number().int(),  // tags resolved to a session (changed or not)
  unmatched: z.array(z.string()), // tags with no session (bad pid/seq)
  // per-tag echo for the sync script's notifications: only tags whose verdict
  // CHANGED, with the moderator pid — lets the script DM «your sessions were
  // invoiced» without a second query.
  changes: z.array(
    z.object({
      tag: z.string(),
      status: KidsClientStatusSchema,
      invoiced: z.boolean(),
      modPid: z.string().nullable(),
    }),
  ),
});
export type KidsExportClientStatusRes = z.infer<typeof KidsExportClientStatusResSchema>;

// POST /api/kids/export/v1/notify-mods {notifications} → the WORKER delivers
// per-moderator DMs through the plan bot (env.TELEGRAM_BOT_TOKEN): moderators
// bind their chat via the bot's /start webhook, so the worker owns both the
// token and the chat ids. The packer (verdict sync / pack reports) calls this
// instead of messaging directly. EXPORT_TOKEN-gated machine channel.
export const KidsExportNotifyModsReqSchema = z.object({
  notifications: z
    .array(
      z.object({
        pid: z.string().trim().regex(/^\d{2}$/, 'pid = 2 digits'),
        text: z.string().min(1).max(3500),
      }),
    )
    .min(1)
    .max(100),
});
export type KidsExportNotifyModsReq = z.infer<typeof KidsExportNotifyModsReqSchema>;

export const KidsExportNotifyModsResSchema = z.object({
  ok: z.literal(true),
  sent: z.number().int(),
  unbound: z.array(z.string()), // pids with no bot binding (tg_chat_id NULL)
});
export type KidsExportNotifyModsRes = z.infer<typeof KidsExportNotifyModsResSchema>;

export const KidsExportCaptureSchema = z.object({
  lighting: KidsLightingSchema,
  lux: z.number().int(),
  faceExpression: z.string(),
  loggedTs: z.number().int().nullable(), // phone clock → packer matches clips
});

export const KidsExportSessionSchema = z.object({
  sessionId: z.number().int(),
  status: z.string(),
  packed: z.boolean(),
  // Clips actually uploaded to R2 by the moderator (sessions.uploaded_at set).
  // `status='done'` means SHOT, not shipped — the packer must not treat a
  // done-but-not-uploaded session as packable (03.07: a CORS-failed upload day
  // leaked a clipless session into the packer pull). Optional for back-compat.
  uploaded: z.boolean().optional(),
  startedAt: z.number().int(),
  endedAt: z.number().int().nullable(),
  office: z.string().nullable(),
  // Authoritative cluster slug from offices.cluster_slug ('atlanta' | 'remote';
  // null if the office is unknown/unseeded). The packer routes tracker rows by
  // cluster — it must NOT infer cluster from the raw office id (managers create
  // new remote offices, so any hardcoded office→cluster map in the packer would
  // silently misroute them). Packer maps 'atlanta'→its atl_dul tracker, 'remote'→World.
  cluster: z.string().nullable(),
  date: z.string().nullable(),
  // moderator file tag (2-digit pid). PII-free identifier.
  moderatorPid: z.string().nullable(),
  // all-time 1-based participant sequence within the moderator, zero-padded to 4
  // digits ("0001"; widens past 9999). Packer builds PID = "P"+moderatorPid+seq.
  participantSeq: z.string(),
  child: z.object({
    id: z.string(),
    name: z.string(),
    age: z.number().int().nullable(),
    gender: z.string().nullable(),
    ethnicity: z.string().nullable(),
    skinTone: z.number().int().nullable(),
    hasBeard: z.boolean(),
    hasMustache: z.boolean(),
    usesReadingGlasses: z.boolean(),
    accessoryName: z.string().nullable(),
    scheduledTime: z.string().nullable(),
    // parental/processing consent captured. V1: always true; later derived from
    // kids_icf_log (Atlanta) + Remote intake processing-consent.
    consentTaken: z.boolean(),
  }),
  captures: z.array(KidsExportCaptureSchema),
});
export type KidsExportSession = z.infer<typeof KidsExportSessionSchema>;

export const KidsExportSessionsResSchema = z.object({
  date: z.string(),
  office: z.string().nullable(),
  sessions: z.array(KidsExportSessionSchema),
});
export type KidsExportSessionsRes = z.infer<typeof KidsExportSessionsResSchema>;
